import { cn } from '@/lib/utils';
import type { HealthStatus, WOImpact, WOStatus, BlockedReason, WOType, MoldStatus } from '@/types/maintenance';

interface StatusBadgeProps {
  status: HealthStatus;
  label?: string;
  size?: 'sm' | 'md';
}

export function StatusBadge({ status, label, size = 'md' }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
        status === 'green' && 'status-green',
        status === 'amber' && 'status-amber',
        status === 'red' && 'status-red'
      )}
    >
      <span
        className={cn(
          'rounded-full',
          size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
          status === 'green' && 'bg-status-green',
          status === 'amber' && 'bg-status-amber',
          status === 'red' && 'bg-status-red'
        )}
      />
      {label || (status === 'green' ? 'Good' : status === 'amber' ? 'Warning' : 'Critical')}
    </span>
  );
}

interface ImpactBadgeProps {
  impact?: WOImpact;
  size?: 'sm' | 'md';
}

const impactLabels: Record<WOImpact, string> = {
  line_down: 'Line Down',
  quality: 'Quality',
  safety: 'Safety',
  performance: 'Performance',
  pm: 'PM',
};

export function ImpactBadge({ impact, size = 'md' }: ImpactBadgeProps) {
  if (!impact) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        impact === 'line_down' && 'priority-critical',
        impact === 'quality' && 'priority-high',
        impact === 'safety' && 'priority-high',
        impact === 'performance' && 'priority-medium',
        impact === 'pm' && 'priority-low'
      )}
    >
      {impactLabels[impact]}
    </span>
  );
}

/** @deprecated Use ImpactBadge instead */
export const PriorityBadge = ({ priority, size }: { priority: WOImpact; size?: 'sm' | 'md' }) => (
  <ImpactBadge impact={priority} size={size} />
);

interface WOStatusBadgeProps {
  status: WOStatus;
  blockedReason?: BlockedReason;
  size?: 'sm' | 'md';
}

const statusLabels: Record<WOStatus, string> = {
  requested: 'Requested',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  pending_review: 'Pending Review',
  closed: 'Closed',
};

const blockedReasonLabels: Record<BlockedReason, string> = {
  parts: 'Parts',
  qa_hold: 'QA Hold',
  supplier: 'Supplier',
  waiting_approval: 'Approval',
  no_manpower: 'No Manpower',
  other: 'Other',
};

export function WOStatusBadge({ status, blockedReason, size = 'md' }: WOStatusBadgeProps) {
  const getStatusColor = () => {
    switch (status) {
      case 'closed':
        return 'bg-[hsl(var(--status-green-bg))] text-[hsl(var(--status-green))]';
      case 'in_progress':
        return 'bg-molten/10 text-molten-deep';
      case 'blocked':
        return 'bg-[hsl(var(--status-red-bg))] text-[hsl(var(--status-red))]';
      case 'pending_review':
        return 'bg-[hsl(var(--status-amber-bg))] text-[hsl(var(--status-amber))]';
      case 'requested':
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        getStatusColor()
      )}
    >
      {statusLabels[status]}
      {status === 'blocked' && blockedReason && (
        <span className="opacity-60">({blockedReasonLabels[blockedReason]})</span>
      )}
    </span>
  );
}

interface WOTypeBadgeProps {
  type: WOType;
  size?: 'sm' | 'md';
}

const typeLabels: Record<WOType, string> = {
  corrective: 'Corrective',
  preventive: 'Preventive',
  improvement: 'Improvement',
  corrective_in_machine: 'In-Machine',
};

export function WOTypeBadge({ type, size = 'md' }: WOTypeBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        type === 'corrective' && 'bg-[hsl(var(--status-red-bg))] text-[hsl(var(--status-red))]',
        type === 'corrective_in_machine' && 'bg-[hsl(var(--status-amber-bg))] text-[hsl(var(--status-amber))]',
        type === 'preventive' && 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
        type === 'improvement' && 'bg-muted text-muted-foreground'
      )}
    >
      {typeLabels[type]}
    </span>
  );
}

interface MoldStatusBadgeProps {
  status: MoldStatus;
  size?: 'sm' | 'md';
}

const moldStatusLabels: Record<MoldStatus, string> = {
  available: 'Available',
  in_production: 'In Production',
  in_maintenance: 'In Maintenance',
  on_hold: 'On Hold',
};

export function MoldStatusBadge({ status, size = 'md' }: MoldStatusBadgeProps) {
  const getColor = () => {
    switch (status) {
      case 'available':
        return 'bg-[hsl(var(--status-green-bg))] text-[hsl(var(--status-green))]';
      case 'in_production':
        return 'bg-molten/10 text-molten-deep';
      case 'in_maintenance':
        return 'bg-[hsl(var(--status-amber-bg))] text-[hsl(var(--status-amber))]';
      case 'on_hold':
        return 'bg-[hsl(var(--status-red-bg))] text-[hsl(var(--status-red))]';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        getColor()
      )}
    >
      {moldStatusLabels[status]}
    </span>
  );
}
